import { useEffect, useRef, useState } from 'react';

import { Button } from '@appcharge/shared-ui';
import useLocalization from 'api/useLocalization';
import { AxiosError } from 'axios';
import { UploadTypes } from 'constants/enums';
import { Upload } from 'lucide-react';
import { formatDate } from 'utils/formattingUtil';

import ImportCSVDrawer from 'components/ImportCSVDrawer/ImportCSVDrawer';

import { EToasterStatus, showToast } from '../../../../utils/showToast';

import './style.scss';

const ImportTranslationsCSVSection = () => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [activeUploadType, setActiveUploadType] = useState<UploadTypes | null>(
    null
  );
  const [lastUploadDateStatic, setLastUploadDateStatic] = useState<
    string | null
  >(null);
  const [lastUploadDateDynamic, setLastUploadDateDynamic] = useState<
    string | null
  >(null);
  const {
    importStaticTranslationsCsvFile,
    importDynamicTranslationsCsvFile,
    getDynamicTranslationsCsvLastUpdateDate,
    getStaticTranslationsCsvLastUpdateDate
  } = useLocalization();

  useEffect(() => {
    const updateLastUpdatedDate = async () => {
      const lastUpdateDateStatic =
        await getStaticTranslationsCsvLastUpdateDate.refetch();
      const lastUpdateDateDynamic =
        await getDynamicTranslationsCsvLastUpdateDate.refetch();
      if (lastUpdateDateStatic.data.lastUpdate) {
        setLastUploadDateStatic(
          formatDate(lastUpdateDateStatic.data.lastUpdate)
        );
      }
      if (lastUpdateDateDynamic.data.lastUpdate) {
        setLastUploadDateDynamic(
          formatDate(lastUpdateDateDynamic.data.lastUpdate)
        );
      }
    };
    updateLastUpdatedDate();
  }, []);

  const handleStaticImportCSV = (formData: FormData) => {
    importStaticTranslationsCsvFile.mutate(formData, {
      onSuccess: async () => {
        showToast({
          message: 'Static translations file has been uploaded successfully',
          status: EToasterStatus.SUCCESS
        });
        const updatedDate =
          await getStaticTranslationsCsvLastUpdateDate.refetch();
        if (updatedDate.data.lastUpdate) {
          setLastUploadDateStatic(formatDate(updatedDate.data.lastUpdate));
        }
        setActiveUploadType(null);
      },
      onError(error) {
        showToast({
          message: 'Static translations file has failed to upload',
          status: EToasterStatus.ERROR,
          description: (
            (error as AxiosError).response?.data as {
              message: string;
            }
          ).message
        });
      }
    });
  };

  const handleDynamicImportCSV = (formData: FormData) => {
    importDynamicTranslationsCsvFile.mutate(formData, {
      onSuccess: async () => {
        showToast({
          message: 'Dynamic translations file has been uploaded successfully',
          status: EToasterStatus.SUCCESS
        });
        const updatedDate =
          await getDynamicTranslationsCsvLastUpdateDate.refetch();
        if (updatedDate.data.lastUpdate) {
          setLastUploadDateDynamic(formatDate(updatedDate.data.lastUpdate));
        }
        setActiveUploadType(null);
      },
      onError(error) {
        showToast({
          message: 'Dynamic translations file has failed to upload',
          status: EToasterStatus.ERROR,
          description: (
            (error as AxiosError).response?.data as {
              message: string;
            }
          ).message
        });
      }
    });
  };

  return (
    <div className="mt-5 mb-9">
      <div className="border border-base-sidebarBorder rounded-lg bg-base-background">
        <div className="px-9 pt-9 pb-9 flex items-start justify-between gap-6 max-md:flex-col max-md:items-stretch">
          <div className="flex-1">
            <p className="text-[20px] text-h6 font-semibold text-base-foreground mb-3">
              Upload translations
            </p>
            <p className="text-p-s text-base-mutedForeground max-w-[720px]">
              Upload CSV files for static and dynamic string translations. Each file should <br /> follow the downloaded format, with original strings and language columns.
            </p>
          </div>
          <div className="flex gap-3 shrink-0 max-md:justify-start">
            <Button
              variant="outline"
              onClick={() => setActiveUploadType(UploadTypes.DYNAMIC)}
              leftIcon={<Upload className="h-4 w-4" />}
            >
              Dynamic Strings
            </Button>
            <Button
              variant="outline"
              onClick={() => setActiveUploadType(UploadTypes.STATIC)}
              leftIcon={<Upload className="h-4 w-4" />}
            >
              Static Strings
            </Button>
          </div>
        </div>
        {(lastUploadDateStatic || lastUploadDateDynamic) && (
          <div className="px-9 pb-9 text-p-s text-base-mutedForeground">
            {lastUploadDateStatic && (
              <span>Last upload: {lastUploadDateStatic} | File type: static</span>
            )}
            {lastUploadDateStatic && lastUploadDateDynamic && (
              <span className="mx-2">•</span>
            )}
            {lastUploadDateDynamic && (
              <span>Last upload: {lastUploadDateDynamic} | File type: dynamic</span>
            )}
          </div>
        )}
      </div>
      <ImportCSVDrawer
        isOpen={!!activeUploadType}
        onCloseDrawer={() => setActiveUploadType(null)}
        fileRef={fileRef}
        onImportFile={(formData: FormData) =>
          activeUploadType === UploadTypes.STATIC
            ? handleStaticImportCSV(formData)
            : handleDynamicImportCSV(formData)
        }
        fileKeyName="languagesFile"
        uploadType={activeUploadType === UploadTypes.STATIC ? 'static' : 'dynamic'}
      />
    </div>
  );
};

export default ImportTranslationsCSVSection;
